// The panels around the hunt scene: the team rail on the left (the lead with
// its XP, the bench with where each one's XP comes from, a shrines summary),
// automation on the right, and over the scene the hunt card with its area's
// other hunts, a suggestion when another hunt pays clearly more, the combat log
// and the session tracker (what this sitting has earned and spent, per hour).
import { ascendMark } from './ascend.ts';
import { CONFIG } from '../shared/config.ts';
import { SPECIES, caughtLevel } from '../shared/species.ts';
import { GROUND, GROUNDS, AREA, REGIONS, groundUnlocked, type GroundDef } from '../shared/grounds.ts';
import { ITEM, ITEMS, REVIVE, REVIVE_HP, shopPriceAt } from '../shared/items.ts';
import { SHRINE_TIERS, SHRINES_IN_USE, SHARDS_PER_SHRINE, CHARM_SHARE } from '../shared/shrines.ts';
import { creatureXpToNext, trainerXpToNext, canFight, killXp } from '../shared/xp.ts';
import { GUILD_BOOST_MULT } from '../shared/guilds.ts';
import { HALLS, KEEPER_TITLE } from '../shared/halls.ts';
import { ELEMENTS, type Element } from '../shared/elements.ts';
import { chatSel, dmWaiting } from './messages.ts';
import { CHAT_CHANNELS, type CreatureDTO, type GameEvent, type ChatChannel, type Settings } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { speciesCatchChance } from '../shared/capture.ts';
import { newTrainerChance } from '../shared/starter.ts';
import { spriteUrl, iconUrl, creatureIcon, uiIcon } from './assets.ts';
import { myArt } from './skins.ts';
import { esc, num, full, big, elChip, cname, nameHtml, summary, duration, estimate, teamPerk, RISK_TEXT, RISK_CLASS } from './fmt.ts';
import { emoteImg } from './kinhall.ts';
import { EMOTES, ownsEmote } from '../shared/kinhall.ts';
import { creatureGrade, inlineMarks, gradeBadge, crest } from './badges.ts';
import { GRADE_CUTOFFS, gradeOf, type Grade } from '../shared/grade.ts';
import { ICON } from './icons.ts';
import { patch } from './dom.ts';
import { batterySaver } from './field.ts';
import { rollPills, miniPills } from './rollpills.ts';
import { socialWaiting } from './social.ts';
import { setSnapshot } from './hovercard.ts';
import { UNLOCKS, unlocked, areasBetween, type Unlock } from '../shared/unlocks.ts';
import { RANKED } from '../shared/ranked.ts';
import { EGG } from '../shared/eggs.ts';
import { FAV_GRADES, FAV_TALENTS, FAV_QUALITIES, POTION_BELOW_CHOICES, firstInStock, sortByStrength, type UseKind, type UseRow } from '../shared/automation.ts';
import { PASS, passActive, nameColorOf, seasonAt, passXp, passLevel, passClaimable, hasPlus } from '../shared/pass.ts';
import { RESTOCK, RESTOCK_ITEMS, vipOn } from '../shared/restock.ts';
import { passLeft } from './pass.ts';
import { MOVE } from '../shared/moves.ts';
import { charmKind, charmUntil } from '../shared/auracharm.ts';
import { catchCharmEffect, catchCharmMult, catchCharmUntil } from '../shared/catchcharm.ts';
import { frameOf } from '../shared/frames.ts';
import { PAID_BOOST, PAID_BOOST_KINDS, paidUntil, multX, pctX } from '../shared/boosts.ts';
import { lateJoinerOn } from '../shared/latejoiner.ts';
import { auraOneInNow } from './auracharm.ts';
import { effectWord } from './moveclock.ts';
import { moveBarHtml, syncMoveBar } from './watch.ts';
import { eventChips } from './events.ts';
import { liveChip } from './streams.ts';
import { cornerOpen, cornerSupported } from './corner/index.ts';
import { mySprite } from './trainerart.ts';

const POPOUT = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" stroke-linecap="round"><path d="M13 4h7v7M20 4l-8 8"/><path d="M18 14v6H4V6h6"/></svg>';

const $ = (sel: string) => document.querySelector(sel) as HTMLElement | null;
const wide = () => window.matchMedia('(min-width: 1400px)').matches;

/**
 * What a level opens, as the live switches have it (the menus' lock lines in client/ui.ts agree): ranked seasons open
 * the Arena and the Battle Plan at their own level, and with the Trail on the bosses stand on it.
 */
export function opensBetween(from: number, to: number): Unlock[] {
  const lv = (u: Unlock) => (S.ranked && (u.id === 'arena' || u.id === 'plan') ? RANKED.unlockLevel : u.level);
  return UNLOCKS.filter((u) => lv(u) > from && lv(u) <= to && (u.id !== 'trail' || S.trailOn))
    .map((u) => (u.id === 'bosses' && S.trailOn ? { ...u, where: 'the Trail' } : u));
}

function bar(frac: number, kind: 'hp' | 'xp', size: 'big' | 'thin', label = '', value = '') {
  return `<div class="bar ${kind} ${size}"><i style="width:${Math.max(0, Math.min(1, frac)) * 100}%"></i>${label || value ? `<span>${label}<em>${value}</em></span>` : ''}</div>`;
}

// ---------------- team rail ----------------
/** The creature fighting now, or the one that will when the hunt starts. */
export function leadCreature(): CreatureDTO | undefined {
  const t = S.trainer;
  if (!t) return undefined;
  const f = S.field;
  if (f?.lead) return S.creatures.get(f.lead.id);
  for (const id of t.team) { const c = S.creatures.get(id); if (c && canFight(c, t.level)) return c; }
  return S.creatures.get(t.team[0]);
}

const SHRINE_SHORT = (tier: number) => SHRINE_TIERS[tier].name.replace(' Shrine', '');

/** Where a bench creature's XP comes from: the larger of its best shrine slot and an XP Charm, or nothing. */
export function xpSource(id: number, leadId: number | undefined): { text: string; share: number; tier?: number } | null {
  const t = S.trainer!;
  if (id === leadId) return null;
  let best: { text: string; share: number; tier?: number } | null = null;
  if (t.team.includes(id)) {
    for (const s of t.shrines ?? []) {
      if (!s.active || !s.posts.includes(id)) continue;
      const tier = SHRINE_TIERS[s.tier];
      if (!best || tier.share > best.share) best = { text: `${SHRINE_SHORT(s.tier)} +${Math.round(tier.share * 100)}% XP`, share: tier.share, tier: s.tier };
    }
  }
  if ((t.charmed ?? []).includes(id) && (!best || CHARM_SHARE > best.share)) best = { text: `XP Charm +${Math.round(CHARM_SHARE * 100)}%`, share: CHARM_SHARE };
  return best;
}

/** XP the lead gets per kill on the current hunt: the last real kill, or the hunt's base before the first one. */
function leadKillXp() {
  const g = GROUND[S.trainer!.ground];
  return lastGain || (g ? killXp(g.level) : 0);
}

/** Why a team member's XP bar is still, or what fills it: shown on the bar's hover, never as a badge. */
/** Whether a team member earns XP right now: the lead, or one fed by a shrine slot or an XP Charm. */
function earnsXp(c: CreatureDTO, leadId: number | undefined, sits: boolean) {
  return !sits && (c.id === leadId || !!xpSource(c.id, leadId));
}

const peekBtn = (id: number) => `<button class="peek-btn" data-act="peek" data-id="${id}" aria-label="Details" title="Details">${uiIcon('ui-info', ICON.info, 'ui-ic', 24)}</button>`;

export function renderTeam() {
  const host = $('#teamlist');
  if (!host || !S.trainer) return;
  const t = S.trainer;
  const f = S.field;
  const g = GROUND[t.ground];
  const lead = leadCreature();
  const leadId = lead?.id;
  const order = lead && t.team.includes(lead.id) ? [lead.id, ...t.team.filter((x) => x !== lead.id)] : [...t.team];
  const slots = order.map((id) => {
    const c = S.creatures.get(id);
    if (!c) return '';
    const hp = f?.teamHp[id];
    const frac = hp ? hp.hp / hp.maxHp : 1;
    const sits = !canFight(c, t.level);
    const need = creatureXpToNext(c.level);
    const earning = earnsXp(c, leadId, sits);
    // Drag to reorder; a click (or the info button) opens the creature's popup over the hunt.
    const drag = `draggable="true" data-team-id="${id}" data-act="peek" data-id="${id}" data-key="c${id}"`;
    if (id === leadId) {
      const per = leadKillXp();
      const spk = g ? estimate(c, g).secondsPerKill : 0;
      const eta = per > 0 && spk > 0 ? duration(Math.ceil((need - c.xp) / per) * spk * 1000) : '';
      return `<div class="slot lead" ${drag}>
        <div class="lead-top">${creatureIcon(myArt(c.species, c.rolls.aura), c.rolls.aura)}
          <div class="grow"><div class="r1"><b class="nm">${nameHtml(c)}</b>${peekBtn(id)}</div>
          <div class="r2"><span class="lv">Lv ${c.level}</span>${ascendMark(c)}${SPECIES[c.species].elements.map(elChip).join('')}</div><div class="lead-mini">${miniPills(c)}</div></div></div>
        ${rollPills(c, { noLevel: true })}
        ${bar(frac, 'hp', 'big', 'HP', hp ? `${full(hp.hp)} / ${full(hp.maxHp)}` : '')}
        <div class="xpwrap" title="${esc(`+${full(per)} XP per kill${eta ? ` · next level in ${eta}` : ''}`)}">${bar(c.xp / need, 'xp', 'big', 'XP', `${full(c.xp)} / ${full(need)}`)}</div>
        ${moveBarHtml(c)}</div>`;
    }
    const pctW = (x: number) => (Math.max(0, Math.min(1, x)) * 100).toFixed(1);
    return `<div class="slot bench${sits ? ' out' : ''}${frac <= 0 ? ' fainted' : ''}" ${drag}>
      ${creatureIcon(myArt(c.species, c.rolls.aura), c.rolls.aura)}
      <div class="grow"><div class="r1"><b class="nm">${nameHtml(c)}</b><span class="lv">Lv ${c.level}</span>${ascendMark(c)}${frac <= 0 ? '<span class="ko">Fainted</span>' : ''}${peekBtn(id)}</div>
        ${miniPills(c)}
        <div class="slivers"><span class="sl hp" title="HP ${hp ? `${full(hp.hp)} / ${full(hp.maxHp)}` : 'full'}"><i style="width:${pctW(frac)}%"></i></span><span class="sl xp${earning ? '' : ' idle'}" title="XP ${full(c.xp)} / ${full(need)}"><i style="width:${pctW(c.xp / need)}%"></i></span></div></div></div>`;
  }).join('');
  // Open places fold into one row, so a short team never makes the rail longer.
  const open = Math.max(0, CONFIG.teamSize - t.team.length);
  const empty = open ? `<a class="slot empty" href="#/creatures" data-key="e">+ ${open} open</a>` : '';
  patch(host, `<div class="phead"><h4>Team <span class="count">${t.team.length} / ${CONFIG.teamSize}</span></h4><a class="link" href="#/creatures/team">Edit team</a></div>
    <div class="slots">${slots}${empty}</div>`);
  syncMoveBar();
  renderPlayerCard();
  renderShrineSummary();
  renderBoosts();
}

// ---------------- player card ----------------
/**
 * Above the team: who you are at a glance (outfit, name, guild tag, trainer level and XP), opening the player
 * screen, with three small doors into it: friends (with what waits for you), your guild and settings.
 */
export function renderPlayerCard() {
  const host = $('#playercard');
  if (!host || !S.trainer) return;
  const t = S.trainer;
  const need = trainerXpToNext(t.level);
  const frac = Math.max(0, Math.min(1, t.xp / need));
  const face = spriteUrl(`${mySprite()}-down-0`);
  const waiting = socialWaiting();
  // The card frame (shared/frames.ts): the pass frame while the pass runs, unless another owned frame is chosen.
  const frame = frameOf(t, serverNow());
  patch(host, `<div class="pc${frame ? ` pc-${frame}` : ''}" data-key="pc"><a class="pc-link" href="#/trainer">
    <span class="pc-face" style="background-image:url('${face}')" aria-hidden="true"></span>
    <span class="pc-main"><span class="pc-name">${t.guild ? `<span class="gtag">[${esc(t.guild.tag)}]</span>` : ''}<b style="color:${nameColorOf(t, serverNow()) ?? 'inherit'}">${esc(t.name)}</b></span>
      <span class="pc-lv"><span class="pc-l">Lv <b>${full(t.level)}</b></span><span class="pc-xp" title="Trainer XP ${full(t.xp)} / ${full(need)}"><i style="width:${(frac * 100).toFixed(1)}%"></i></span><span class="pc-pct">${Math.floor(frac * 100)}%</span></span></span></a>
    <span class="pc-doors"><a class="pc-door" href="#/trainer/friends" title="Friends${waiting ? `: ${waiting} waiting` : ''}" aria-label="Friends">${uiIcon('ui-friends', ICON.friends, 'ui-ic', 32)}${waiting ? `<span class="dot">${waiting > 9 ? '9+' : waiting}</span>` : ''}</a><a class="pc-door" href="#/guild" title="${t.guild ? `Guild: ${esc(t.guild.name)}` : 'Guild'}" aria-label="Guild">${uiIcon('ui-guild', ICON.guild, 'ui-ic', 32)}</a><a class="pc-door" href="#/trainer/settings" title="Settings" aria-label="Settings">${uiIcon('ui-gear', ICON.gear, 'ui-ic', 32)}</a></span></div>${perkRows()}${boostChips()}`);
}

// ---------------- active boosts ----------------
/** "42m 10s", "5h 12m", "29d 4h": time left on a boost. */
function boostLeft(ms: number) {
  const s = Math.max(0, Math.ceil(ms / 1000));
  if (s >= 2 * 86_400) return `${Math.floor(s / 86_400)}d ${Math.floor((s % 86_400) / 3600)}h`;
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

/**
 * Every boost running now, the XP Charms in use and the shrines, as small chips on the player card: the name and
 * time left on each, the full effect on hover. It keeps the rail short enough to never scroll.
 */
function boostChips(): string {
  const t = S.trainer!;
  const now = serverNow();
  const pctOf = (m: number) => `+${Math.round((m - 1) * 100)}%`;
  const chips: string[] = [];
  // Server events show first, whatever the level: free for everyone.
  const ev = eventChips(now, GROUND[t.ground]?.region ?? '');
  if (ev) chips.push(ev);
  // Partner streams (client/streams.ts): who is live, or the watch bonus while the dock plays one.
  const live = liveChip();
  if (live) chips.push(live);
  const chip = (key: string, name: string, rest: string, tip: string, href = '', cls = '') => {
    const tag = href ? 'a' : 'span';
    return `<${tag} class="bchip ${cls}" data-key="${key}"${href ? ` href="${href}"` : ''} title="${esc(tip)}"><b>${name}</b>${rest ? `<em>${rest}</em>` : ''}</${tag}>`;
  };
  // Late Joiner boost (shared/latejoiner.ts): bought, so shown whatever the level, with its target.
  if (t.lateJoiner && lateJoinerOn(t)) chips.push(chip('latejoiner', `Late Joiner ×${CONFIG.lateJoiner.mult}`, `Lv ${full(t.level)} / ${full(t.lateJoiner.target)}`, `Late Joiner: ${multX(CONFIG.lateJoiner.mult)} until Lv ${full(t.lateJoiner.target)}`, '#/shop/diamonds', 'paidb'));
  // Paid boosts (shared/boosts.ts): bought, so shown whatever the level.
  for (const k of PAID_BOOST_KINDS) {
    const until = paidUntil(t.boosts, k);
    if (until > now) chips.push(chip(`paid-${k}`, PAID_BOOST[k].short, boostLeft(until - now), `Boost: ${PAID_BOOST[k].effect} for ${boostLeft(until - now)}`, '#/shop/diamonds', 'paidb'));
  }
  // The season pass and VIP have their own rows above the chips (perkRows).
  // The Aura Charm, like the pass, shows whatever the level: an icon and the time left.
  const charm = charmKind(t.boosts, now);
  if (charm) {
    const rest = boostLeft(charmUntil(t.boosts, now) - now);
    chips.push(`<a class="bchip ac-chip ac-${charm}" data-key="aura" href="#/shop/diamonds" title="${esc(`Aura Charm: 1 in ${full(auraOneInNow())} catches for ${rest}`)}" aria-label="Aura Charm">${ICON.aura}<em>${rest}</em></a>`);
  }
  // The Catch Charm (earned in ranked seasons) shows the same way, whatever the level.
  const catchUntil = catchCharmUntil(t.boosts, now);
  if (catchUntil) chips.push(chip('catchcharm', 'Catch Charm', boostLeft(catchUntil - now), `Catch Charm: ${catchCharmEffect()} per throw for ${boostLeft(catchUntil - now)}`, '#/bag/supplies', 'cc-chip'));
  if (unlocked(t, 'boosts', now)) {
    if (t.boosts.xpUntil > now && !(paidUntil(t.boosts, 'txp') > now && paidUntil(t.boosts, 'cxp') > now)) chips.push(chip('xp', 'XP', boostLeft(t.boosts.xpUntil - now), `Boost: ${multX(CONFIG.xpBoostMult)} for ${boostLeft(t.boosts.xpUntil - now)}`, '#/shop/diamonds'));
    if (t.boosts.coinUntil > now && !(paidUntil(t.boosts, 'loot') > now)) chips.push(chip('coin', 'Coins', boostLeft(t.boosts.coinUntil - now), `Boost: ${multX(CONFIG.coinBoostMult, 'coins')} for ${boostLeft(t.boosts.coinUntil - now)}`, '#/shop/diamonds'));
    if (t.guild && t.guild.boostUntil > now) chips.push(chip('guild', 'Guild', boostLeft(t.guild.boostUntil - now), `Guild: ${multX(GUILD_BOOST_MULT)} for ${boostLeft(t.guild.boostUntil - now)}`, '#/guild'));
    if (t.guild?.war && t.guild.war.until > now) chips.push(chip('war', 'War', `×${+(1 + t.guild.war.pct / 100).toFixed(2)}`, `Guild War: ${pctX(t.guild.war.pct, 'XP and loot')} for ${boostLeft(t.guild.war.until - now)}`, '#/guild'));
    // The Hall Keeper bonus lasts as long as the hall is held.
    if (t.halls?.length) {
      const names = t.halls.map((e) => ELEMENTS[e as Element]?.name ?? e);
      chips.push(chip('halls', KEEPER_TITLE, names.length === 1 ? names[0] : `${names.length}`, `${KEEPER_TITLE} of ${names.join(', ')}: ${pctOf(HALLS.keeperMult)} damage with creatures of ${names.length === 1 ? 'that element' : 'those elements'} on the hunt and against bosses, while you keep the hall`, '#/halls'));
    }
    const inUse = (t.charmed ?? []).length, spare = t.bag['xp-charm'] ?? 0;
    if (inUse || spare) chips.push(chip('charm', `Charm ×${inUse}`, spare ? `${spare} spare` : '', `XP Charm: each holder gets ${Math.round(CHARM_SHARE * 100)}% of the lead's kill XP. ${inUse} in use${spare ? `, ${spare} spare in the bag` : ''}.`, '#/creatures/team', inUse ? '' : 'idle'));
  }
  // Shrine Shards drop from Hellas hunts: until then the chip only adds noise.
  if (unlocked(t, 'shrines', now)) {
    const shrines = t.shrines ?? [];
    const shards = t.bag['shrine-shard'] ?? 0;
    const used = shrines.filter((x) => x.active).length;
    chips.push(shrines.length
      ? chip('shrines', 'Shrines', `${used} / ${SHRINES_IN_USE}`, `Shrines ${used}/${SHRINES_IN_USE} in use`, '#/creatures/team/shrines')
      : chip('shrines', 'Shrines', `${full(shards)} / ${SHARDS_PER_SHRINE} shards`, shards >= SHARDS_PER_SHRINE ? 'Ready to build' : `${SHARDS_PER_SHRINE} shards build a shrine`, shards >= SHARDS_PER_SHRINE ? '#/creatures/team/shrines' : '#/help/shrines', shards >= SHARDS_PER_SHRINE ? 'ready' : 'idle'));
  }
  // Nothing running: no line at all (the diamond shop is one click away in the menus).
  return chips.length ? `<div class="bchips" data-key="chips">${chips.join('')}</div>` : '';
}

/**
 * The season pass and VIP as rows on the player card, pokeidle-style (Cole, 2026-10-20): always there, so the pass
 * is one click from the hunt and both say what they give. Running, a row shows its effect and time left;
 * not bought, it is dimmed and opens the pass page or VIP's shop card.
 */
function perkRows(): string {
  const t = S.trainer!;
  const now = serverNow();
  const row = (key: string, href: string, on: boolean, name: string, effect: string, rest: string, dot = false) =>
    `<a class="perk perk-${key}${on ? ' on' : ''}" data-key="perk-${key}" href="${href}"><b>${name}</b><span class="perk-fx">${effect}</span><em>${rest}</em>${dot ? '<i class="dot"></i>' : ''}</a>`;
  const season = seasonAt(now);
  const pass = season ? (passActive(t, now)
    ? row('pass', '#/pass', true, `${esc(season.name)} Pass${hasPlus(t, now, season) ? '+' : ''}`, `${multX(PASS.xpMult)}`, passLeft(now), passClaimable(t, now))
    : row('pass', '#/pass', false, `${esc(season.name)} Pass`, `Tier ${passLevel(passXp(t, now)).tier}`, 'Unlock', passClaimable(t, now))) : '';
  const vipOnNow = t.boosts.vipUntil > now;
  const vip = row('vip', '#/shop/diamonds/vip', vipOnNow, 'VIP', `+${Math.round((CONFIG.vipXpMult - 1) * 100)}% XP`, vipOnNow ? boostLeft(t.boosts.vipUntil - now) : 'Get', false);
  return `<div class="perks" data-key="perks">${pass}${vip}</div>`;
}

/** The boosts now live on the player card; the old panel stays hidden. */
function renderBoosts() {
  const host = $('#boostsum');
  if (host) host.hidden = true;
}

/** Shrines now show as a chip in the boosts line; the old one-line panel stays hidden. */
function renderShrineSummary() {
  const host = $('#shrinesum');
  if (host) host.hidden = true;
}

/** Drag a bench slot onto another slot: it takes that place in the team order (onto the lead: it leads). */
let dragId = 0;
export function mountTeamDrag(send: (ids: number[]) => void) {
  document.addEventListener('dragstart', (e) => {
    const el = (e.target as HTMLElement).closest?.('[data-team-id]') as HTMLElement | null;
    if (!el) return;
    dragId = Number(el.dataset.teamId);
    e.dataTransfer?.setData('text/plain', String(dragId));
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    el.classList.add('dragging');
  });
  document.addEventListener('dragend', () => { dragId = 0; document.querySelectorAll('.dragging, .dropto').forEach((n) => n.classList.remove('dragging', 'dropto')); });
  document.addEventListener('dragover', (e) => {
    const el = (e.target as HTMLElement).closest?.('[data-team-id]') as HTMLElement | null;
    if (!el || !dragId) return;
    e.preventDefault();
    document.querySelectorAll('.dropto').forEach((n) => n !== el && n.classList.remove('dropto'));
    el.classList.add('dropto');
  });
  document.addEventListener('drop', (e) => {
    const el = (e.target as HTMLElement).closest?.('[data-team-id]') as HTMLElement | null;
    if (!el || !dragId || !S.trainer) return;
    e.preventDefault();
    dropOn(el, dragId, send);
  });
  touchDrag(send);
}

/** Drop creature `id` on slot `el`: it takes that place in the team order (onto the lead: it leads). */
function dropOn(el: HTMLElement, id: number, send: (ids: number[]) => void) {
  if (!S.trainer) return;
  const to = Number(el.dataset.teamId);
  if (to === id) return;
  const team = S.trainer.team.filter((x) => x !== id);
  const at = el.classList.contains('lead') ? 0 : team.indexOf(to);
  team.splice(Math.max(0, at), 0, id);
  send(team);
}

/**
 * Touch screens have no drag and drop: press and hold a slot, then slide it onto another. A quick tap still opens
 * the creature; a swipe before the hold still scrolls.
 */
const HOLD_MS = 280;
function touchDrag(send: (ids: number[]) => void) {
  let t: { el: HTMLElement; id: number; x: number; y: number; timer: number; on: boolean; ghost: HTMLElement | null } | null = null;
  const stop = () => {
    if (!t) return;
    clearTimeout(t.timer);
    t.ghost?.remove();
    document.querySelectorAll('.dragging, .dropto').forEach((n) => n.classList.remove('dragging', 'dropto'));
    t = null;
  };
  const under = (x: number, y: number) => (document.elementFromPoint(x, y) as HTMLElement | null)?.closest?.('[data-team-id]') as HTMLElement | null;
  document.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse' || !e.isPrimary) return;
    const el = (e.target as HTMLElement).closest?.('[data-team-id]') as HTMLElement | null;
    if (!el || (e.target as HTMLElement).closest('button:not([data-team-id])')) return;
    stop();
    const cur = { el, id: Number(el.dataset.teamId), x: e.clientX, y: e.clientY, timer: 0, on: false, ghost: null as HTMLElement | null };
    cur.timer = window.setTimeout(() => {
      if (t !== cur || !el.isConnected) return;
      cur.on = true;
      el.classList.add('dragging');
      // A copy of the slot follows the finger.
      const g = el.cloneNode(true) as HTMLElement;
      const r = el.getBoundingClientRect();
      g.classList.remove('dragging');
      g.classList.add('drag-ghost');
      g.removeAttribute('data-team-id');
      g.style.width = `${r.width}px`;
      g.style.left = `${cur.x - r.width / 2}px`;
      g.style.top = `${cur.y - r.height / 2}px`;
      document.body.appendChild(g);
      cur.ghost = g;
      navigator.vibrate?.(12);
    }, HOLD_MS);
    t = cur;
  });
  document.addEventListener('pointermove', (e) => {
    if (!t || e.pointerType === 'mouse') return;
    if (!t.on) { if (Math.hypot(e.clientX - t.x, e.clientY - t.y) > 8) stop(); return; }
    if (t.ghost) { const w = t.ghost.offsetWidth, h = t.ghost.offsetHeight; t.ghost.style.left = `${e.clientX - w / 2}px`; t.ghost.style.top = `${e.clientY - h / 2}px`; }
    const over = under(e.clientX, e.clientY);
    document.querySelectorAll('.dropto').forEach((n) => n !== over && n.classList.remove('dropto'));
    if (over && Number(over.dataset.teamId) !== t.id) over.classList.add('dropto');
  });
  // While holding, the page must not scroll under the finger.
  document.addEventListener('touchmove', (e) => { if (t?.on) e.preventDefault(); }, { passive: false });
  document.addEventListener('contextmenu', (e) => { if (t) e.preventDefault(); });
  document.addEventListener('pointerup', (e) => {
    if (!t || e.pointerType === 'mouse') return;
    if (t.on) {
      const over = under(e.clientX, e.clientY), id = t.id;
      // The release is not a tap: swallow the click it makes.
      swallowUntil = performance.now() + 400;
      stop();
      if (over) dropOn(over, id, send);
      return;
    }
    stop();
  });
  document.addEventListener('pointercancel', stop);
  document.addEventListener('click', (c) => { if (performance.now() < swallowUntil) { c.stopPropagation(); c.preventDefault(); } }, { capture: true });
}
let swallowUntil = 0;

// ---------------- hunt card and suggestion ----------------
/** When the current hunt started (kept across reloads), so the suggestion waits two minutes on a new hunt. */
function huntSince(ground: string): number {
  try {
    const v = JSON.parse(localStorage.getItem('mi-hunt-since') ?? 'null');
    if (v?.g === ground) return v.at;
    localStorage.setItem('mi-hunt-since', JSON.stringify({ g: ground, at: Date.now() }));
  } catch { /* storage off */ }
  return huntSinceMem.g === ground ? huntSinceMem.at : (huntSinceMem = { g: ground, at: Date.now() }).at;
}
let huntSinceMem = { g: '', at: 0 };

function dismissed(ground: string) {
  try { return (JSON.parse(localStorage.getItem('mi-suggest-off') ?? '{}')[ground] ?? 0) > Date.now(); } catch { return false; }
}
export function dismissSuggestion(ground: string) {
  try {
    const all = JSON.parse(localStorage.getItem('mi-suggest-off') ?? '{}');
    for (const k of Object.keys(all)) if (all[k] < Date.now()) delete all[k];
    all[ground] = Date.now() + 24 * 3_600_000;
    localStorage.setItem('mi-suggest-off', JSON.stringify(all));
  } catch { /* storage off */ }
}

function matchupText(mult: number, lead: CreatureDTO, g: GroundDef) {
  if (mult > 1) return '<span class="risk-safe">Super effective</span>';
  if (mult < 1) return '<span class="risk-ok">Resisted</span>';
  return '<span>Neutral</span>';
}


/** Hellas and Yomi areas 1-10 hold first and second forms that arrive at their capture cap but fight as finals. */
export function earlyFormLine(g: GroundDef) {
  if (g.region === 'midgard' || g.stage === 2) return '';
  return `${g.stage === 0 ? 'First' : 'Second'} form · caught at Lv ${caughtLevel(g.species, g.level)}`;
}

/** Which metric the suggestion compares: XP, or coins when the map is set to coins. */
const metric = () => (S.mapMetric === 'coins' ? 'coinsPerHour' : 'xpPerHour') as 'coinsPerHour' | 'xpPerHour';

/**
 * Another unlocked hunt that pays at least 25% more for the lead, at Safe or
 * Some risk: first in this area, then anywhere else in the region. Never in
 * the first two minutes on a hunt, and not for a day after "Not now".
 */
export function suggestion(): { g: GroundDef; here: boolean; rate: number; cur: number } | null {
  const t = S.trainer;
  const lead = leadCreature();
  const cur = t && GROUND[t.ground];
  if (!t || !lead || !cur) return null;
  if (Date.now() - huntSince(cur.id) < 2 * 60_000 || dismissed(cur.id)) return null;
  const m = metric();
  const base = estimate(lead, cur)[m];
  const pick = (list: GroundDef[]) => {
    let best: GroundDef | null = null, rate = 0;
    for (const g of list) {
      if (g.id === cur.id || !groundUnlocked(g, t.level)) continue;
      const e = estimate(lead, g);
      if (e.risk === 'risky' || e[m] < base * 1.25) continue;
      if (e[m] > rate) { best = g; rate = e[m]; }
    }
    return best ? { g: best, rate } : null;
  };
  const here = pick(GROUNDS.filter((g) => g.area === cur.area));
  if (here) return { ...here, here: true, cur: base };
  const near = pick(GROUNDS.filter((g) => g.region === cur.region && g.area !== cur.area));
  return near ? { ...near, here: false, cur: base } : null;
}

export function renderHuntCard() {
  const host = $('#fieldhead');
  const t = S.trainer;
  if (!host || !t) return;
  const g = GROUND[t.ground];
  if (!g) return;
  const sp = SPECIES[g.species];
  const lead = leadCreature();
  const e = lead ? estimate(lead, g) : null;
  const region = REGIONS.find((r) => r.id === g.region)!;
  // Themes tint their chrome from the hunt's region (public/themes/*.css read [data-region]).
  if (document.documentElement.dataset.region !== g.region) document.documentElement.dataset.region = g.region;
  const early = earlyFormLine(g);
  const sug = suggestion();
  const hunts = GROUNDS.filter((x) => x.area === g.area);
  // Top row: where you are, and the two scene tools at one size. Then the wild, its numbers, and this area's hunts.
  patch(host, `<div class="hc-top"><a class="crumb" href="#/map/${g.region}/${g.area}">${esc(region.name)} › ${esc(AREA[g.area].name)}</a></div>
    <div class="what"><img src="${spriteUrl(g.species)}" alt=""><b title="${esc(sp.rarity[0].toUpperCase() + sp.rarity.slice(1))} species${early ? ` · ${esc(early)}` : ''}">${esc(sp.name)}</b><span class="lvl">Lv ${g.level}</span>${sp.elements.map(elChip).join('')}</div>
    ${e && lead ? `<div class="facts">${matchupText(e.mult, lead, g)}<span class="dot">·</span><span>${e.secondsPerKill.toFixed(1)} s/kill</span><span class="dot">·</span><span class="${RISK_CLASS[e.risk]}">${RISK_TEXT[e.risk]}</span>${sug ? `<button class="pill share narrow-only" data-act="suggest-show" title="${esc(SPECIES[sug.g.species].name)} earns about ${num(sug.rate)} ${metric() === 'xpPerHour' ? 'XP' : 'coins'}/h">${ICON.bolt}Faster hunt ${sug.here ? 'here' : 'nearby'}</button>` : ''}</div>` : ''}
    ${hunts.length > 1 ? `<div class="seg">${hunts.map((x) => `<button class="${x.id === g.id ? 'on' : ''}" data-act="switch-hunt" data-id="${x.id}" title="${esc(SPECIES[x.species].name)} · Lv ${x.level}"${x.id === g.id ? ' aria-current="true"' : ''}><img src="${spriteUrl(x.species)}" alt=""><span>${esc(shortName(SPECIES[x.species].name))}</span></button>`).join('')}</div>` : ''}`);
  renderSuggest(sug, lead);
  renderStopped();
  renderFieldTools(g);
}

/** Fainted with an empty bag (TrainerDTO.stopped): the team is in Kinhall until a hunt is picked. */
function renderStopped() {
  const host = $('#stopped');
  if (!host) return;
  const t = S.trainer!;
  host.hidden = !t.stopped;
  if (!t.stopped) return;
  patch(host, `<b class="st-h">Your team fainted</b>
    <div class="muted">${t.settings.autoPotion ? 'Out of potions and Revives' : 'No Revives left and Auto potion is off'} · back in Kinhall</div>
    <div class="acts">${stockButton(t.settings.potions.find((r) => r.on && ITEM[r.id])?.id ?? 'mead', 10)}${stockButton(REVIVE, 5)}</div>
    <div class="acts"><a class="btn small ghost" href="#/kinhall">Kinhall</a><button class="btn small primary" data-act="hunt-again">Hunt again</button></div>`);
}

/** Whether the hunt's emote row is open (the Emotes field tool). */
let emotesOpen = false;
export function toggleHuntEmotes(open = !emotesOpen) { emotesOpen = open; }
/** The scene's own tools in its top-right corner, one column of square icon buttons: map, battery saver, pop out, emotes, zoom. */
function renderFieldTools(g: GroundDef) {
  const host = $('#fieldtools');
  if (!host) return;
  patch(host, `<a class="ft" href="#/map/${g.region}/${g.area}" title="Map (M)" aria-label="Map" data-key="map">${uiIcon('ui-map', ICON.map, 'ui-ic', 24)}</a>
    <button class="ft" data-act="saver-on" title="Battery saver" aria-label="Battery saver" data-key="saver">${uiIcon('ui-saver', ICON.saver, 'ui-ic', 24)}</button>
    ${cornerSupported() ? `<button class="ft" data-act="corner" title="Pop out (P)" aria-label="Pop out the hunt" aria-pressed="${cornerOpen()}" data-key="corner">${uiIcon('ui-popout', POPOUT, 'ui-ic', 24)}</button>` : ''}
    <span class="ft-wrap" data-key="emotes"><button class="ft" data-act="hunt-emotes" title="Emotes" aria-label="Emotes" aria-expanded="${emotesOpen}">${emoteImg('wave', 24, 'ui-ic')}</button>${emotesOpen ? `<span class="ft-emotes" role="toolbar" aria-label="Emotes">${EMOTES.filter((e) => ownsEmote(S.trainer!, e.id)).map((e) => `<button class="ft" data-act="hunt-emote" data-id="${e.id}" title="${esc(e.name)}" aria-label="${esc(e.name)}">${emoteImg(e.id, 24, 'ui-ic')}</button>`).join('')}</span>` : ''}</span>
    <span class="ft-gap" data-key="gap"></span>
    <button class="ft" data-act="zoom-in" title="Zoom in" aria-label="Zoom in" data-key="zi">+</button>
    <button class="ft" data-act="zoom-out" title="Zoom out" aria-label="Zoom out" data-key="zo">−</button>`);
}

/** "Yggdrasil Runner" reads "Runner" in the switcher; the full name is on hover. */
const shortName = (n: string) => (n.length > 12 && n.includes(' ') ? n.slice(n.lastIndexOf(' ') + 1) : n);

let suggestOpen = false;
export function showSuggestion() { suggestOpen = true; }

function renderSuggest(sug: ReturnType<typeof suggestion>, lead: CreatureDTO | undefined) {
  const host = $('#suggest');
  if (!host) return;
  // Below 1400 px the card waits behind the pill in the hunt card.
  const show = !!sug && !!lead && (wide() || suggestOpen);
  host.hidden = !show;
  if (!sug) suggestOpen = false;
  if (!show || !sug || !lead) return;
  const g = sug.g, sp = SPECIES[g.species], e = estimate(lead, g);
  const unit = metric() === 'xpPerHour' ? 'XP/h' : 'coins/h';
  patch(host, `<div class="t">${ICON.bolt}Faster ${metric() === 'xpPerHour' ? 'XP' : 'coins'} ${sug.here ? 'in this area' : 'nearby'}</div>
    <b>${esc(sp.name)}</b>${sug.here ? '' : ` · ${esc(AREA[g.area].name)}`} · <b>${num(sug.rate)} ${unit}</b> <span class="muted">(now ${num(sug.cur)})</span> · ${e.mult > 1 ? 'Super effective' : e.mult < 1 ? 'Resisted' : 'Neutral'} · ${RISK_TEXT[e.risk]}
    <div class="acts"><button class="btn small primary" data-act="switch-hunt" data-id="${g.id}">${sug.here ? 'Switch hunt' : 'Travel there'}</button><button class="btn small ghost" data-act="suggest-dismiss" data-id="${S.trainer!.ground}">Not now</button></div>`);
}

// ---------------- automation ----------------
/** Off, C, or C and B: the score below which new catches are released. An older custom value stays selectable. */
function releaseOptions(cur: number) {
  const b = GRADE_CUTOFFS.find(([g]) => g === 'B')![1], a = GRADE_CUTOFFS.find(([g]) => g === 'A')![1];
  const opts: [number, string][] = [[0, 'Off'], [b, 'C'], [a, 'C or B']];
  if (!opts.some(([v]) => v === cur)) opts.push([cur, `Score under ${cur}`]);
  return opts.map(([v, n]) => `<option value="${v}"${cur === v ? ' selected' : ''}>${n}</option>`).join('');
}

/** Recent throw times, for "seals run out in ~6 min". */
const throws: number[] = [];
function throwsPerMinute() {
  const since = Date.now() - 10 * 60_000;
  const recent = throws.filter((x) => x >= since);
  if (recent.length < 3) return 0;
  return (recent.length * 60_000) / Math.max(60_000, Date.now() - recent[0]);
}

/** Chance one seal of this kind catches on the current hunt, with the team's catch bonuses and a running Catch Charm. */
export function sealChance(seal: string): number {
  const t = S.trainer!;
  const g = GROUND[t.ground];
  if (!g) return 0;
  const team = t.team.map((id) => S.creatures.get(id)).filter((c): c is CreatureDTO => !!c);
  return newTrainerChance(t, g.species, speciesCatchChance(g.species, ITEM[seal]?.catchMult ?? 1, teamPerk('catch', team) * catchCharmMult(t.boosts, serverNow())));
}
export const pct = (x: number) => `${(x * 100).toFixed(x < 0.1 ? 2 : 1)}%`;

/** "Running", "Resting 8 s" or "Paused · offline", for the automation header and the hunt strip. */
export function huntState(): { text: string; cls: 'good' | 'amber' | 'bad' } {
  if (!S.online) return { text: 'Paused · offline', cls: 'bad' };
  if (S.trainer?.stopped) return { text: 'In Kinhall', cls: 'bad' };
  const f = S.field;
  const rest = f ? f.restingUntil - serverNow() : 0;
  if (rest > 0) return { text: `Resting ${Math.ceil(rest / 1000)} s`, cls: 'amber' };
  return { text: 'Running', cls: 'good' };
}

/** Only shown when it matters: the bag is empty or runs out within ten minutes. */
function sealWarning() {
  const t = S.trainer!;
  const rows = t.settings.seals.filter((r) => r.on);
  const seal = ITEM[rows[0]?.id ?? ''];
  if (!seal) return t.settings.autoCatch === 'off' ? '' : '<div class="warn bad">No seal on</div>';
  const have = rows.reduce((a, r) => a + (t.bag[r.id] ?? 0), 0);
  const buy = buyButton(seal.id, 50);
  if (have <= 0) return `<div class="warn bad">Out of seals ${buy}</div>`;
  const rate = throwsPerMinute();
  if (rate > 0 && have / rate < 10) return `<div class="warn">Seals out in ~${Math.max(1, Math.round(have / rate))} min ${buy}</div>`;
  return '';
}

function potionWarning() {
  const t = S.trainer!;
  if (!t.settings.autoPotion) return '';
  const rows = t.settings.potions.filter((r) => r.on);
  const potion = ITEM[rows[0]?.id ?? ''];
  if (!potion) return '<div class="warn">No potion on</div>';
  if (rows.some((r) => (t.bag[r.id] ?? 0) > 0)) return '';
  return `<div class="warn">Out of potions ${buyButton(potion.id, 10)}</div>`;
}

/** "[icon] 10 · 5,100": a buy button that shows which item by its icon. */
const stockButton = (id: string, n: number) => {
  const cost = n * (shopPriceAt(id, S.trainer!.level) ?? 0);
  return `<button class="btn small st-buy" data-act="buy" data-id="${id}" data-qty="${n}" title="Buy ${n} ${esc(ITEM[id].name)}"${S.trainer!.coins < cost ? ' disabled' : ''}><img src="${iconUrl(id)}" alt="">${n} · ${full(cost)}</button>`;
};

const buyButton = (id: string, n: number) => {
  const cost = n * (shopPriceAt(id, S.trainer!.level) ?? 0);
  return `<button class="btn tiny" data-act="buy" data-id="${id}" data-qty="${n}"${S.trainer!.coins < cost ? ' disabled' : ''}>Buy ${n} · ${full(cost)}</button>`;
};

const toggle = (key: string, on: boolean, label: string) =>
  `<label class="line"><input type="checkbox" class="toggle" data-setting-bool="${key}"${on ? ' checked' : ''}><span>${label}</span></label>`;

// The panel shows what runs now (Throw at, Auras, the seal and potion in use, Heal below); the lists, selling and
// auto-move fold into Advanced rows that remember, per trainer, whether they were open.
export type AutoSection = 'order' | 'selling' | 'fav' | 'move' | 'restock';
let adv: { who: string; open: Record<AutoSection, boolean> } | null = null;
function advOpen(): Record<AutoSection, boolean> {
  const who = S.trainer?.name ?? '';
  if (adv?.who === who) return adv.open;
  let open: Record<AutoSection, boolean> = { order: false, selling: false, fav: false, move: false, restock: false };
  try { const v = JSON.parse(localStorage.getItem(`mi-auto-adv:${who}`) ?? 'null'); if (v && typeof v === 'object') open = { order: !!v.order, selling: !!v.selling, fav: !!v.fav, move: !!v.move, restock: !!v.restock }; } catch { /* storage off */ }
  adv = { who, open };
  return open;
}
export function toggleAutoSection(id: string) {
  if (id !== 'order' && id !== 'selling' && id !== 'fav' && id !== 'move' && id !== 'restock') return;
  const open = advOpen();
  open[id] = !open[id];
  try { localStorage.setItem(`mi-auto-adv:${S.trainer?.name ?? ''}`, JSON.stringify(open)); } catch { /* storage off */ }
}

/** The list is strongest first: the "Best first" switch reads on. */
const bestFirst = (kind: UseKind, rows: readonly UseRow[]) => sortByStrength(rows, kind, true).every((r, i) => r.id === rows[i].id);

/**
 * The seal or potion list in Advanced: used top to bottom, skipping rows that are off or empty. Drag a row by its
 * grip (or focus the grip and use the arrow keys) to reorder; "Best first" sorts it strongest first in one tap.
 */
function useList(kind: UseKind, rows: readonly UseRow[], label: string) {
  const t = S.trainer!;
  const g = GROUND[t.ground];
  const items = rows.map((r) => {
    const it = ITEM[r.id];
    const n = t.bag[r.id] ?? 0;
    const tip = kind === 'seals' && g ? ` title="${pct(sealChance(r.id))} per throw here"` : '';
    return `<li class="${r.on ? 'on' : 'off'}${n ? '' : ' empty'}" data-key="${r.id}" data-use-row="${r.id}"${tip}>
      <button class="grip" data-grip="${kind}" data-id="${r.id}" aria-label="Reorder ${esc(it.name)}: drag, or use the arrow keys">${ICON.grip}</button>
      <label><input type="checkbox" data-use="${kind}" data-id="${r.id}"${r.on ? ' checked' : ''} aria-label="Use ${esc(it.name)}"><img src="${iconUrl(r.id)}" alt=""><span class="nm">${esc(it.name)}</span></label>
      <span class="n">${num(n)}</span></li>`;
  }).join('');
  return `<div class="usesort"><span>${label}</span><label class="best"><input type="checkbox" class="toggle sm" data-best="${kind}"${bestFirst(kind, rows) ? ' checked' : ''}> Best first</label></div>
    <ol class="uselist" data-uselist="${kind}" aria-label="${label}">${items}</ol>`;
}

/** Reordering, switches and "Best first" on a seal or potion list. Returns the new list to send. */
export function useListChange(kind: UseKind, op: 'move' | 'sort' | 'switch' | 'order', id: string, arg: number | boolean | string | string[]): UseRow[] {
  const rows = S.trainer!.settings[kind].map((r) => ({ ...r }));
  if (op === 'sort') return sortByStrength(rows, kind, arg === 'strong');
  if (op === 'order' && Array.isArray(arg)) return arg.map((x) => rows.find((r) => r.id === x)).filter((r): r is UseRow => !!r).concat(rows.filter((r) => !arg.includes(r.id)));
  const i = rows.findIndex((r) => r.id === id);
  if (i < 0) return rows;
  if (op === 'switch') rows[i].on = !!arg;
  else { const j = i + Number(arg); if (j >= 0 && j < rows.length) [rows[i], rows[j]] = [rows[j], rows[i]]; }
  return rows;
}

/** Drag a seal or potion row by its grip; the order is sent on release. Arrow keys on a focused grip move it one place. */
export function mountUseDrag(send: (kind: UseKind, rows: UseRow[]) => void) {
  let drag: { kind: UseKind; li: HTMLElement; list: HTMLElement; id: number } | null = null;
  document.addEventListener('pointerdown', (e) => {
    const grip = (e.target as HTMLElement).closest?.('[data-grip]') as HTMLElement | null;
    if (!grip) return;
    const li = grip.closest('li') as HTMLElement, list = grip.closest('[data-uselist]') as HTMLElement;
    drag = { kind: grip.dataset.grip === 'potions' ? 'potions' : 'seals', li, list, id: e.pointerId };
    li.classList.add('dragging');
    grip.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  });
  document.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const rows = [...drag.list.children] as HTMLElement[];
    const over = rows.find((r) => r !== drag!.li && e.clientY > r.getBoundingClientRect().top && e.clientY < r.getBoundingClientRect().bottom);
    if (!over) return;
    const after = rows.indexOf(over) > rows.indexOf(drag.li);
    drag.list.insertBefore(drag.li, after ? over.nextSibling : over);
  });
  const end = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return;
    const { kind, list, li } = drag;
    drag = null;
    li.classList.remove('dragging');
    const ids = [...list.children].map((r) => (r as HTMLElement).dataset.useRow ?? '');
    if (ids.join() !== S.trainer!.settings[kind].map((r) => r.id).join()) send(kind, useListChange(kind, 'order', '', ids));
  };
  document.addEventListener('pointerup', end);
  document.addEventListener('pointercancel', end);
  document.addEventListener('keydown', (e) => {
    const grip = (e.target as HTMLElement).closest?.('[data-grip]') as HTMLElement | null;
    if (!grip || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    e.preventDefault();
    const kind: UseKind = grip.dataset.grip === 'potions' ? 'potions' : 'seals';
    send(kind, useListChange(kind, 'move', grip.dataset.id ?? '', e.key === 'ArrowUp' ? -1 : 1));
    requestAnimationFrame(() => (document.querySelector(`[data-grip="${kind}"][data-id="${grip.dataset.id}"]`) as HTMLElement | null)?.focus());
  });
}

/** The seal or potion in use now, compact: icon, short name and count. A click opens the order. */
function inUse(kind: UseKind) {
  const t = S.trainer!, st = t.settings;
  const id = firstInStock(st[kind], t.bag);
  const off = kind === 'potions' && !st.autoPotion;
  const it = id ? ITEM[id] : null;
  const what = kind === 'seals' ? 'Seal' : 'Potion';
  const tip = !it ? '' : kind === 'seals' && GROUND[t.ground] ? `${it.name} · ${pct(sealChance(it.id))} per throw` : it.name;
  return `<button class="ause${it ? '' : ' none'}${off ? ' off' : ''}" data-act="auto-sec" data-id="order" title="${esc(tip)}">
    ${it ? `<img src="${iconUrl(it.id)}" alt="">` : '<span class="noimg"></span>'}<span class="nm">${it ? esc(it.name.replace(/ Seal$/, '')) : `No ${what.toLowerCase()}s`}</span><b>${it ? num(t.bag[it.id] ?? 0) : '0'}</b></button>`;
}

const THROW_AT: [string, string][] = [['all', 'Every wild'], ['new', 'Until I have one'], ['rare', 'Rare and up'], ['off', 'Nothing']];
const MOVE_TO: [string, string][] = [['off', 'Stay on this hunt'], ['xp', 'Best Safe hunt for XP'], ['coins', 'Best Safe hunt for coins']];

const FAV_GRADE_OPTS: [string, string][] = [['off', 'Off'], ...FAV_GRADES.map((g): [string, string] => [g, g === 'S+' ? 'S+' : `${g} and up`])];
const FAV_TALENT_OPTS: [string, string][] = [['0', 'Off'], ...FAV_TALENTS.map((v): [string, string] => [String(v), v === 100 ? '100%' : `${v}% and up`])];
const FAV_QUALITY_OPTS: [string, string][] = [['0', 'Off'], ...FAV_QUALITIES.map((v): [string, string] => [String(v), `×${v.toFixed(2)} and up`])];
const favSelect = (key: string, label: string, opts: [string, string][], cur: string) =>
  `<label class="line">${label} <select data-setting="${key}" aria-label="Auto-favorite ${label.toLowerCase()}">${opts.map(([v, n]) => `<option value="${v}"${cur === v ? ' selected' : ''}>${n}</option>`).join('')}</select></label>`;
/** The Auto-favorite row's summary: which rules are on. */
function favNow(st: Settings) {
  const on = [st.favAuras !== false ? 'Auras' : '', st.favBloodline ? `BL ${CONFIG.bloodline[2].name}+` : '', st.favGrade && st.favGrade !== 'off' ? (st.favGrade === 'S+' ? 'S+' : `${st.favGrade}+`) : '', st.favLegendary ? 'Legendary' : '', st.favTalent ? `Talent ${st.favTalent}%+` : '', st.favQuality ? `Quality ×${st.favQuality.toFixed(2)}+` : ''].filter(Boolean);
  return on.join(' · ') || 'Off';
}

/** VIP perks in Automation (auto-restock, auto-move): without VIP the row is greyed with a VIP seal and opens VIP in the shop. */
const vipNow = (t: NonNullable<typeof S.trainer>) => vipOn(t, serverNow());

/** VIP auto-restock (shared/restock.ts): a switch, how many of each to keep, and a coin floor. */
function restockNow(t: NonNullable<typeof S.trainer>) {
  return t.settings.restock?.on ? 'On' : 'Off';
}
function restockBody(t: NonNullable<typeof S.trainer>) {
  const r = t.settings.restock;
  const rows = RESTOCK_ITEMS.map((id) => `<label class="line rs-row"><img src="${iconUrl(id)}" alt=""><span>${esc(ITEM[id].name)}</span><span class="muted">${num(t.bag[id] ?? 0)} now</span>
    <input type="number" min="0" max="${RESTOCK.maxKeep}" step="1" data-restock="keep" data-id="${id}" value="${r?.keep[id] ?? 0}" aria-label="Keep ${esc(ITEM[id].name)}"></label>`).join('');
  return `<label class="line"><input type="checkbox" class="toggle" data-restock="on"${r?.on ? ' checked' : ''}><span>Top up with coins</span></label>
    <div class="rs-keep"><div class="muted">Keep</div>${rows}</div>
    <label class="line">Never below <input type="number" min="0" step="1000" data-restock="floor" value="${r?.floor ?? 0}" aria-label="Coin floor"> coins</label>`;
}

/** Auto-move, a VIP perk too. */
function moveNow(t: NonNullable<typeof S.trainer>) {
  return (MOVE_TO.find(([v]) => v === (t.settings.autoMove ?? 'off'))?.[1] ?? 'Stay').replace('Stay on this hunt', 'Stay');
}
function moveBody(t: NonNullable<typeof S.trainer>) {
  const cur = t.settings.autoMove ?? 'off';
  return `<label class="line">Auto-move <select data-setting="autoMove" aria-label="Auto-move">${MOVE_TO.map(([v, n]) => `<option value="${v}"${cur === v ? ' selected' : ''}>${n}</option>`).join('')}</select></label>`;
}

/** One Advanced row: a button with the section's name and its current setting; the body shows when open. vip: a VIP perk the trainer lacks, so the row links to VIP instead. */
function advRow(id: AutoSection, name: string, now: string, body: () => string, vip = false) {
  if (vip) return `<div class="adv vip-row" data-key="adv-${id}"><a class="adv-h" href="#/shop/diamonds/vip" aria-label="${name}: get VIP in the diamond shop"><span class="chev" aria-hidden="true"></span><span class="nm">${name}</span><span class="now"><span class="vip-seal" aria-hidden="true">VIP</span></span></a></div>`;
  const open = advOpen()[id];
  return `<div class="adv${open ? ' open' : ''}" data-key="adv-${id}"><button class="adv-h" data-act="auto-sec" data-id="${id}" aria-expanded="${open}"><span class="chev" aria-hidden="true"></span><span class="nm">${name}</span><span class="now">${now}</span></button>
    ${open ? `<div class="adv-b">${body()}</div>` : ''}</div>`;
}

export function renderAuto() {
  const host = $('#auto');
  if (!host || !S.trainer) return;
  const t = S.trainer;
  const st = t.settings;
  const hs = huntState();
  const below = st.autoPotion ? st.potionBelow : 0;
  const release = st.autoSellBelow ?? 0;
  const sellNow = release ? `Release ${releaseOptions(release).match(/selected>([^<]*)/)?.[1] ?? ''}` : 'Off';
  const cheapFirst = (kind: UseKind) => sortByStrength(st[kind], kind, false).every((r, i) => r.id === st[kind][i].id);
  const order = bestFirst('seals', st.seals) ? 'Best first' : cheapFirst('seals') ? 'Cheapest first' : 'Custom';
  patch(host, `<div class="phead"><h4>Automation</h4><span class="pill ${hs.cls}">${hs.text}</span></div>
    <div class="grp now">
      <label class="line">Throw at <select data-setting="autoCatch" aria-label="Throw at">
        ${THROW_AT.map(([v, n]) => `<option value="${v}"${st.autoCatch === v ? ' selected' : ''}>${n}</option>`).join('')}
      </select></label>
      <div class="ainuse">${inUse('seals')}${inUse('potions')}</div>
      <label class="line">Heal below <select data-potion-below aria-label="Heal below">
        ${[0, ...POTION_BELOW_CHOICES].map((v) => `<option value="${v}"${below === v ? ' selected' : ''}>${v ? `${v}% HP` : 'Never'}</option>`).join('')}
      </select></label>
      ${sealWarning()}${potionWarning()}
    </div>
    <div class="advs">
      ${advRow('order', 'Seal and potion order', order, () => `${useList('seals', st.seals, 'Seals')}${useList('potions', st.potions, 'Potions')}`)}
      ${advRow('selling', 'Selling', sellNow, () => `<label class="line">Release grade <select data-setting="autoSellBelow" aria-label="Release grade">${releaseOptions(release)}</select></label>`)}
      ${advRow('fav', 'Auto-favorite', favNow(st), () => `<div class="autofav">${toggle('favAuras', st.favAuras !== false, 'Auras')}${toggle('favBloodline', !!st.favBloodline, `Bloodline ${CONFIG.bloodline[2].name}+`)}${toggle('favLegendary', !!st.favLegendary, 'Legendary')}</div>
        <label class="line">Grade <select data-setting="favGrade" aria-label="Auto-favorite grade">${FAV_GRADE_OPTS.map(([v, n]) => `<option value="${v}"${(st.favGrade ?? 'off') === v ? ' selected' : ''}>${n}</option>`).join('')}</select></label>
        ${favSelect('favTalent', 'Talent', FAV_TALENT_OPTS, String(st.favTalent ?? 0))}${favSelect('favQuality', 'Quality', FAV_QUALITY_OPTS, String(st.favQuality ?? 0))}`)}
      ${advRow('restock', 'Auto-restock', restockNow(t), () => restockBody(t), !vipNow(t))}
      ${advRow('move', 'Auto-move', moveNow(t), () => moveBody(t), !vipNow(t))}
    </div>`);
}

/**
 * Right rail tabs below 1400 px (Automation, Chat), and the chat panel's
 * channel tabs at every width: World (chat plus the big-catch feed), Trade,
 * Guild and Help. A channel shows how many lines arrived since you last had it open.
 */
let rtab: 'auto' | 'chat' = 'auto';
export const isChannel = (v: unknown): v is ChatChannel => (CHAT_CHANNELS as readonly unknown[]).includes(v);
let chan: ChatChannel = (() => { try { const v = localStorage.getItem('mi-chan'); return isChannel(v) ? v : 'world'; } catch { return 'world'; } })();
export function setRailTab(tab: string) {
  if (isChannel(tab)) {
    chan = tab; rtab = 'chat';
    try { localStorage.setItem('mi-chan', tab); } catch { /* storage off */ }
  } else rtab = tab === 'chat' ? 'chat' : 'auto';
  renderRailTabs();
}
export const feedChannel = () => chan;
/** The region of the hunt now running: the Region channel talks to everyone hunting there. */
export function huntRegion(): { id: string; name: string } | null {
  const id = S.field?.ground ?? S.trainer?.ground;
  const r = id ? GROUND[id]?.region : undefined;
  return REGIONS.find((x) => x.id === r) ?? null;
}
/** Newest line seen per channel; lines before the page loaded count as seen. */
const seenAt: Record<string, number> = Object.fromEntries(CHAT_CHANNELS.map((c) => [c, Date.now()]));
export function markSeen(ch: ChatChannel, ts: number) { if (ts > (seenAt[ch] ?? 0)) seenAt[ch] = ts; }
export function unread(ch: ChatChannel) {
  let n = 0;
  for (let i = S.broadcasts.length - 1; i >= 0; i--) {
    const b = S.broadcasts[i];
    if (b.ts <= (seenAt[ch] ?? 0)) break;
    if (b.kind === 'chat' && (b.ch ?? 'world') === ch && b.who !== S.trainer?.name && (b.rg === undefined || b.rg === huntRegion()?.id)) n++;
  }
  return n;
}

/** A channel's tab name; Region is named for the region of the running hunt. */
export const channelName = (id: ChatChannel) => (id === 'region' ? huntRegion()?.name ?? 'Region' : { world: 'World', trade: 'Trade', guild: 'Guild', help: 'Help' }[id]);

export function renderRailTabs() {
  const rail = $('#rcol');
  if (!rail) return;
  rail.dataset.rtab = rtab;
  const hs = huntState();
  const sel = chatSel();
  const dm = dmWaiting();
  const waiting = CHAT_CHANNELS.reduce((a, c) => a + (c === chan && rtab === 'chat' && sel.k === 'ch' ? 0 : unread(c)), 0) + dm.unread;
  patch($('#rtabs'), `${[['auto', 'Automation'], ['chat', 'Chat']].map(([id, n]) => `<button class="${rtab === id ? 'on' : ''}" data-act="rtab" data-id="${id}">${n}${id === 'chat' && waiting ? ` <span class="unread">${waiting > 99 ? '99+' : waiting}</span>` : ''}</button>`).join('')}<span class="spacer"></span><span class="pill ${hs.cls}">${hs.text}</span>`);
  // Channels, then your conversations behind the envelope (client/messages.ts).
  const tabs = CHAT_CHANNELS.map((id) => { const on = sel.k === 'ch' && chan === id; const n = on ? 0 : unread(id); return `<button class="${on ? 'on' : ''}" data-act="rtab" data-id="${id}" aria-pressed="${on}">${channelName(id)}${n ? ` <span class="unread" aria-label="${n} new">${n > 99 ? '99+' : n}</span>` : ''}</button>`; }).join('');
  const dmOn = sel.k !== 'ch';
  const env = `<button class="ft-dm${dmOn ? ' on' : ''}" data-act="rtab" data-id="inbox" aria-pressed="${dmOn}" title="Messages" aria-label="Messages${dm.unread ? `, ${dm.unread} unread` : ''}">${ICON.mail}${dm.unread ? `<span class="unread">${dm.unread > 99 ? '99+' : dm.unread}</span>` : dm.requests ? '<i class="dot"></i>' : ''}</button>`;
  patch($('#feedtabs'), tabs + env + (S.playersOnline ? `<span class="players-narrow">${S.playersOnline.toLocaleString()} online</span>` : ''));
}

// ---------------- session tracker ----------------
interface Session {
  start: number;
  kills: number;
  /** Trainer XP from kills (gross) and what knockouts took back. */
  xp: number; xpLost: number; knockouts: number;
  /** Kill coins plus coins from auto-released catches. */
  coins: number;
  catches: number; grades: Record<string, number>; aurasSeen: number; aurasCaught: number;
  /** XP the bench got from shrine slots and XP Charms, and who got it. */
  benchXp: number; benchIds: number[];
  /** Kill XP per lead, and the level each lead had when it first earned this session. */
  leadXp: Record<string, number>; leadFrom: Record<string, number>;
  seals: Record<string, number>; potions: Record<string, number>;
  /** This session's catches, newest last: the creature as caught, and the coins if it was released on the spot. */
  caught: { c: CreatureDTO; at: number; sold?: number }[];
}
let session: Session = blank();
let sessionKey = '';
let sessionOpen = (() => { try { return localStorage.getItem('mi-session-open') !== '0'; } catch { return true; } })();
/** Below 1400 px the session is a pill until clicked. */
let narrowOpen = false;

function blank(): Session {
  return { start: Date.now(), kills: 0, xp: 0, xpLost: 0, knockouts: 0, coins: 0, catches: 0, grades: {}, aurasSeen: 0, aurasCaught: 0, benchXp: 0, benchIds: [], leadXp: {}, leadFrom: {}, seals: {}, potions: {}, caught: [] };
}

function loadSession() {
  const key = `mi-session:${S.trainer?.name ?? ''}`;
  if (key === sessionKey) return;
  sessionKey = key;
  try { const s = JSON.parse(localStorage.getItem(key) ?? 'null'); session = s && s.start ? { ...blank(), ...s } : blank(); } catch { session = blank(); }
}
let saveAt = 0;
function saveSession() {
  if (Date.now() < saveAt) return;
  saveAt = Date.now() + 2000;
  try { localStorage.setItem(sessionKey, JSON.stringify(session)); } catch { /* storage off */ }
}

export function resetSession() { session = blank(); saveAt = 0; saveSession(); renderSession(); renderLog(); }
export function toggleSession() {
  if (!wide()) { narrowOpen = !narrowOpen; if (narrowOpen) sessionOpen = true; }
  else sessionOpen = !sessionOpen;
  try { localStorage.setItem('mi-session-open', sessionOpen ? '1' : '0'); } catch { /* storage off */ }
  renderSession();
}

/** For the hunt strip and the trainer chip. */
let lastGain = 0;
export const lastKillXp = () => lastGain;
export const sessionCaught = () => { loadSession(); return session.catches; };
/** Session totals, for tests and the trainer page. */
export const sessionTotals = () => { loadSession(); return { ...session }; };
const RATE_AFTER = 5 * 60_000;
/** Trainer XP per hour this session (net of knockouts), once five minutes have passed. */
export function sessionXpPerHour(): number | null { loadSession(); return Date.now() - session.start >= RATE_AFTER ? perHour(session.xp - session.xpLost) : null; }

// ---------------- live rates (the hunt strip) ----------------
/**
 * The strip's per-hour numbers: the same kill, catch, seal and potion events the session panel adds up, kept for the
 * last ten minutes. While the session is younger than that, or this page has not been open that long, the rates are
 * the session's own averages, so the strip and the panel agree.
 */
const RECENT_MS = 10 * 60_000;
type Tick = { at: number; xp: number; coins: number; kills: number; lead?: number; cxp?: number };
const recent: Tick[] = [];
const recentSince = Date.now();
let killSeq = 0;
function noteRecent(t: Omit<Tick, 'at'>) {
  const at = Date.now();
  recent.push({ at, ...t });
  while (recent.length && recent[0].at < at - RECENT_MS) recent.shift();
}
/** Counts kills seen on this page: a new value per kill, for the strip's gain to replay. */
export const killCount = () => killSeq;
export interface LiveRates { xp: number; kills: number; coins: number; lead: number | null; spanMs: number; rolling: boolean }
/** Trainer XP, kills and net coins per hour now; null in the first minute. `lead` is the given creature's XP per hour. */
export function liveRates(leadId?: number): LiveRates | null {
  loadSession();
  const now = Date.now();
  const from = now - RECENT_MS;
  const leadRecent = (span: number) => {
    if (leadId === undefined) return null;
    const xs = recent.filter((r) => r.at >= now - span && r.lead === leadId);
    return xs.length ? (xs.reduce((a, r) => a + (r.cxp ?? 0), 0) * 3_600_000) / span : null;
  };
  if (session.start >= from || recentSince > from) {
    const span = now - session.start;
    if (span < 60_000) return null;
    const lx = leadId !== undefined ? (session.leadXp[leadId] ?? 0) : 0;
    const spent = sessionSpent();
    return {
      xp: perHour(session.xp - session.xpLost), kills: perHour(session.kills), coins: perHour(session.coins - spent.seals - spent.potions),
      lead: leadRecent(Math.min(span, now - recentSince)) ?? (lx ? perHour(lx) : null), spanMs: span, rolling: false,
    };
  }
  const sum = (k: 'xp' | 'coins' | 'kills') => recent.reduce((a, r) => (r.at >= from ? a + r[k] : a), 0);
  const h = (v: number) => (v * 3_600_000) / RECENT_MS;
  return { xp: h(sum('xp')), kills: h(sum('kills')), coins: h(sum('coins')), lead: leadRecent(RECENT_MS), spanMs: RECENT_MS, rolling: true };
}

const perHour = (v: number) => (v * 3_600_000) / Math.max(60_000, Date.now() - session.start);
function clock(ms: number) {
  // A device clock that steps back (a sync, or set by hand) read "-1:-1:-1".
  const s = Math.floor(Math.max(0, ms) / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`;
}
/** Compact with the full value on hover; a sign only when asked, and never "-0". */
const signed = (v: number) => { const r = Math.round(v); return r > 0 ? `+${num(r)}` : r < 0 ? num(r) : '0'; };
const val = (v: number, sign = true) => `<b title="${full(v)}">${sign ? signed(v) : num(v)}</b>`;

function sessionSpent() {
  const lv = S.trainer?.level ?? 1;
  const seals = Object.entries(session.seals).reduce((a, [id, n]) => a + n * (shopPriceAt(id, lv) ?? 0), 0);
  const potions = Object.entries(session.potions).reduce((a, [id, n]) => a + n * (shopPriceAt(id, lv) ?? 0), 0);
  return { seals, potions };
}

export function renderSession() {
  const host = $('#session');
  if (!host || !S.trainer) return;
  loadSession();
  renderSaver();
  const early = Date.now() - session.start < RATE_AFTER;
  const rate = (v: number, unit = '') => early ? '<em title="Rates show after 5 minutes">—</em>' : `<em title="${full(perHour(v))}${unit} per hour">${num(perHour(v))}${unit}/h</em>`;
  const spent = sessionSpent();
  const net = session.coins - spent.seals - spent.potions;
  const netXp = session.xp - session.xpLost;
  const open = sessionOpen && (wide() || narrowOpen);
  if (!open) {
    host.className = 'session pill-mode';
    patch(host, `<button class="spill" data-act="session-toggle"><b>${clock(Date.now() - session.start)}</b> · ${early ? '—' : num(perHour(netXp))} XP/h · ${early ? '—' : num(perHour(net))} coins/h · ${full(session.catches)} caught <span class="muted">▸</span></button>`);
    return;
  }
  host.className = 'session';
  const lead = leadCreature();
  const lx = lead ? session.leadXp[lead.id] ?? 0 : 0;
  const from = lead ? session.leadFrom[lead.id] : undefined;
  const sealsUsed = Object.values(session.seals).reduce((a, b) => a + b, 0);
  const grades = (['S+', 'S', 'A', 'B', 'C'] as Grade[]).filter((g) => session.grades[g]).slice(0, 3)
    .map((g) => `${gradeBadge(GRADE_CUTOFFS.find(([x]) => x === g)![1], 'sm')} ${session.grades[g]}`).join(' ');
  const tr = (label: string, v: string, right: string, cls = '') => `<tr class="${cls}"><td>${label}</td><td>${v}</td><td>${right}</td></tr>`;
  patch(host, `<div class="h"><b>THIS SESSION</b><span class="clock">${clock(Date.now() - session.start)}</span><span class="spacer"></span><button class="btn tiny" data-act="session-reset">Reset</button><button class="btn tiny" data-act="session-toggle" aria-label="Hide session">Hide</button></div>
    <table>
      ${tr('Trainer XP', val(netXp), rate(netXp))}
      ${lead ? tr(`${esc(cname(lead))} XP`, val(lx), from !== undefined && from < lead.level ? `<em>Lv ${from} → ${lead.level}</em>` : `<em>Lv ${lead.level}</em>`) : ''}
      ${session.benchXp || (S.trainer.shrines ?? []).some((s) => s.active) || (S.trainer.charmed ?? []).length ? tr(`Shrine XP (${session.benchIds.length})`, val(session.benchXp), rate(session.benchXp)) : ''}
      ${tr('Coins, net', val(net), rate(net), net < 0 ? 'neg' : '')}
      ${tr('Defeated', val(session.kills, false), rate(session.kills), 'sep')}
      ${tr('Caught', val(session.catches, false), `<em>${grades}</em>`)}
      ${tr('Knockouts', val(session.knockouts, false), `<em>${session.xpLost ? `${num(-session.xpLost)} XP` : 'no XP lost'}</em>`)}
      ${tr('Seals thrown', val(sealsUsed, false), `<em title="${full(spent.seals)} coins">${num(spent.seals)} coins</em>`, 'sep')}
    </table>`);
}

// ---------------- battery saver ----------------
/**
 * In battery saver the scene isn't drawn; this page of big session cards takes
 * its place: what this session earned, per hour, and what that means next
 * (the next level, how often a kill or a catch comes). Built from the same
 * session totals as the panel above.
 */
function renderSaver() {
  const host = $('#saver');
  if (!host) return;
  const on = batterySaver();
  host.hidden = !on;
  if (!on) return;
  const t = S.trainer!;
  const elapsed = Date.now() - session.start;
  const early = elapsed < RATE_AFTER;
  const spent = sessionSpent();
  const net = session.coins - spent.seals - spent.potions;
  const netXp = session.xp - session.xpLost;
  const lead = leadCreature();
  const lx = lead ? session.leadXp[lead.id] ?? 0 : 0;
  const hs = huntState();
  const g = GROUND[t.ground];
  const wait = '<span title="Rates show after 5 minutes">—</span>';
  const hourly = (h: number, unit: string) => early ? wait : `<span title="${full(h)}${unit ? ` ${unit}` : ''} per hour">${full(h)}${unit ? ` ${unit}` : ''}/h</span>`;
  const rate = (v: number, unit: string) => hourly(perHour(v), unit);
  const every = (n: number, what: string) => n >= 3 ? `one every ${secs(elapsed / n)}${what}` : '';
  // Next levels: the trainer at this session's net rate; the lead at its XP per kill times this session's kill rate.
  const tNeed = trainerXpToNext(t.level) - t.xp;
  const tRate = perHour(netXp);
  const tLine = early ? '' : tRate > 0 ? `Lv ${t.level + 1} in ${duration((tNeed / tRate) * 3_600_000)}` : '';
  const kph = perHour(session.kills);
  const lRate = leadKillXp() * kph;
  const lLine = !lead ? '' : early ? '' : lRate > 0 ? `Lv ${lead.level + 1} in ${duration(((creatureXpToNext(lead.level) - lead.xp) / lRate) * 3_600_000)}` : `Lv ${lead.level}`;
  const card = (key: string, label: string, value: string, r: string, line: string, cls = '') =>
    `<div class="sv-card ${cls}" data-key="${key}"><div class="sc-l">${label}</div><b class="sc-v">${value}</b><div class="sc-r">${r}</div><div class="sc-n">${line || '&nbsp;'}</div></div>`;
  const sign = (v: number) => { const r = Math.round(v); return r > 0 ? `+${full(r)}` : r < 0 ? `−${full(-r)}` : '0'; };
  patch(host, `<div class="sv-head" data-key="h"><b class="sv-t">BATTERY SAVER</b><span class="pill ${hs.cls}">${hs.text}</span>
      <span class="sv-clock" title="This session">${clock(elapsed)}</span>${g ? `<span class="muted ellip">${esc(SPECIES[g.species].name)} · Lv ${g.level} · ${esc(AREA[g.area].name)}</span>` : ''}
      <span class="spacer"></span><button class="btn small" data-act="session-reset">Reset session</button><button class="btn small primary" data-act="saver-off">Show the scene</button></div>
    <div class="sv-cards" data-key="c">
      ${card('txp', 'Trainer XP', sign(netXp), rate(netXp, 'XP'), tLine)}
      ${card('lxp', `${lead ? esc(cname(lead)) : 'Lead'} XP`, sign(lx), lead ? hourly(lRate, 'XP') : wait, lLine)}
      ${card('coin', 'Coins, net', sign(net), rate(net, 'coins'), spent.seals + spent.potions ? `−${full(spent.seals + spent.potions)} supplies` : '', net < 0 ? 'neg' : '')}
      ${card('kill', 'Defeated', full(session.kills), rate(session.kills, ''), every(session.kills, ''))}
      ${card('catch', 'Caught', full(session.catches), rate(session.catches, ''), session.catches ? `1 per ${full(Math.round(session.kills / session.catches))} kills` : `${full(Object.values(session.seals).reduce((a, b) => a + b, 0))} thrown`)}
    </div>`);
}

/** "3.2 s", "48 s", "~4 min" for how often something happens. */
function secs(ms: number) { return ms < 60_000 ? `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s` : duration(ms).replace(/^~/, ''); }

// ---------------- combat log ----------------
// Kills of one species merge into one line, and so do misses, for up to 30 s;
// anything else that happens starts fresh lines. 50 lines are kept; the log
// shows the last 6 until clicked.
interface Line {
  id: number; kind: 'kill' | 'miss' | 'text'; cls: string; at: number;
  species?: string; n: number; xp: number; coins: number; drops: Record<string, number>; who?: string;
  seal?: string; text?: string;
}
/** A creature name in a log line opens the hover card. */
const nameLink = (c: CreatureDTO) => `<span class="cn" data-creature-id="${c.id}">${esc(cname(c))}</span>`;
const log: Line[] = [];
let logSeq = 0;
let logOpen = false;
let killLine: Line | null = null, missLine: Line | null = null;
const MERGE_MS = 30_000;

function add(l: Omit<Line, 'id' | 'at' | 'n' | 'xp' | 'coins' | 'drops'> & Partial<Line>): Line {
  const line: Line = { id: ++logSeq, at: Date.now(), n: 1, xp: 0, coins: 0, drops: {}, ...l };
  log.push(line);
  if (log.length > 50) log.shift();
  return line;
}
function push(text: string, cls = '') { killLine = null; missLine = null; add({ kind: 'text', cls, text }); }
/** Moves a merged line to the bottom when something else was logged after it. */
function touch(l: Line) { const i = log.indexOf(l); if (i >= 0 && i < log.length - 2) { log.splice(i, 1); log.push(l); } }

function lineHtml(l: Line) {
  if (l.kind === 'kill') {
    const drops = Object.entries(l.drops).map(([n, q]) => `${esc(n)}${q > 1 ? ` ×${q}` : ''}`).join(', ');
    return `${esc(SPECIES[l.species!]?.name ?? 'Wild')} defeated${l.n > 1 ? ` ×${l.n}` : ''}<span class="xp">+${full(l.xp)} XP</span><span class="coin">+${full(l.coins)} coins</span>${drops ? `<span class="muted">${drops}</span>` : ''}`;
  }
  if (l.kind === 'miss') return `${esc(ITEM[l.seal!]?.name ?? 'Seal')} missed${l.n > 1 ? ` ×${l.n}` : `: ${esc(SPECIES[l.species!]?.name ?? 'it')} broke free`}`;
  return l.text!;
}

// ---------------- the fight line (watch mode) ----------------
// One live line on top of the log for the lead's latest blow: the move, the damage and the match-up in words
// ("strong", "weak"), never a multiplier. It is rewritten in place, so a blow a second never floods the log.
let fight: { mv: string; dmg: number; crit: boolean; mult: number; wild: string; at: number } | null = null;
const FIGHT_SHOWN_MS = 20_000;

function fightHtml() {
  if (!fight || Date.now() - fight.at > FIGHT_SHOWN_MS) return '';
  const m = MOVE[fight.mv];
  const word = effectWord(fight.mult);
  const name = m ? `<b class="fmv" style="--el:${ELEMENTS[m.element].color}">${esc(m.name)}</b>` : 'A blow';
  const tags = [word ? `<span class="${fight.mult > 1 ? 'strong' : 'weak'}">${word}</span>` : '', fight.crit ? '<span class="crit">critical</span>' : ''].filter(Boolean).join(' ');
  return `<div class="fight${m?.slot === 'signature' || m?.slot === 'awakened' ? ' sig' : ''}" data-key="fight">${name} hit ${esc(fight.wild)} for <b>${big(fight.dmg)}</b>${tags ? ` · ${tags}` : ''}</div>`;
}

/** A line from elsewhere (arena results), already escaped HTML. */
export function logLine(html: string, cls = '') { push(html, cls); renderLog(); }

export function toggleLog() { if (logTab === 'log') { logOpen = !logOpen; renderLog(); } }

/** The log box has two tabs: the log, and this session's catches. */
let logTab: 'log' | 'catches' = (() => { try { return localStorage.getItem('mi-logtab') === 'catches' ? 'catches' : 'log'; } catch { return 'log'; } })();
export function setLogTab(tab: string) {
  logTab = tab === 'catches' ? 'catches' : 'log';
  try { localStorage.setItem('mi-logtab', logTab); } catch { /* storage off */ }
  renderLog();
}

export function renderLog() {
  const host = $('#combatlog');
  if (!host) return;
  loadSession();
  host.classList.toggle('open', logOpen && logTab === 'log');
  host.classList.toggle('catches', logTab === 'catches');
  const tabs = `<div class="lg-tabs" data-key="tabs" role="tablist">${[['log', 'Log'], ['catches', `Catches <span class="count">${session.caught.length}</span>`]].map(([id, label]) => `<button role="tab" aria-selected="${logTab === id}" class="${logTab === id ? 'on' : ''}" data-act="logtab" data-id="${id}">${label}</button>`).join('')}</div>`;
  if (logTab === 'catches') { patch(host, tabs + capturesHtml()); return; }
  patch(host, tabs + fightHtml() + log.slice(logOpen ? -50 : -6).map((l) => `<div class="${l.kind === 'miss' ? 'miss' : l.cls}" data-key="l${l.id}">${lineHtml(l)}</div>`).join(''));
}

// ---------------- session catches ----------------
// Every catch of this session with one row of roll pills, sortable. Released
// catches stay in the list, dimmed, and still open the hover card.
const CAUGHT_KEEP = 200;
const CAP_SORTS = [['recent', 'Recent'], ['grade', 'Grade'], ['talent', 'Talent'], ['bloodline', 'Bloodline'], ['quality', 'Quality'], ['level', 'Level']] as const;
type CapSort = typeof CAP_SORTS[number][0];
let capSort: CapSort = (() => { try { const v = localStorage.getItem('mi-capsort'); return (CAP_SORTS.some(([k]) => k === v) ? v : 'recent') as CapSort; } catch { return 'recent'; } })();
export function setCapSort(v: string) {
  if (!CAP_SORTS.some(([k]) => k === v)) return;
  capSort = v as CapSort;
  try { localStorage.setItem('mi-capsort', v); } catch { /* storage off */ }
  renderLog();
}
/** Rolls never change after a catch, so each row's pills and sort keys are worked out once. */
const capCache = new Map<number, { pills: string; score: number; talent: number }>();
function capInfo(c: CreatureDTO) {
  let v = capCache.get(c.id);
  if (!v) {
    const s = summary(c);
    v = { pills: rollPills(c, { rollsOnly: true }), score: s.rating, talent: s.iv };
    if (capCache.size > CAUGHT_KEEP * 2) capCache.clear();
    capCache.set(c.id, v);
  }
  return v;
}

function capturesHtml() {
  const list = session.caught.map((x, i) => ({ ...x, i, info: capInfo(x.c) }));
  type Row = typeof list[number];
  const by: Record<CapSort, (a: Row, b: Row) => number> = {
    recent: (a, b) => b.i - a.i,
    grade: (a, b) => b.info.score - a.info.score || b.i - a.i,
    talent: (a, b) => b.info.talent - a.info.talent || b.i - a.i,
    bloodline: (a, b) => b.c.rolls.bloodline - a.c.rolls.bloodline || b.info.score - a.info.score,
    quality: (a, b) => b.c.rolls.quality - a.c.rolls.quality || b.i - a.i,
    level: (a, b) => b.c.level - a.c.level || b.i - a.i,
  };
  list.sort(by[capSort]);
  const sorts = `<div class="cap-sorts" data-key="sorts" role="group" aria-label="Sort catches">${CAP_SORTS.map(([k, n]) => `<button class="${capSort === k ? 'on' : ''}" aria-pressed="${capSort === k}" data-act="cap-sort" data-id="${k}">${n}</button>`).join('')}</div>`;
  if (!list.length) return `${sorts}<p class="cap-empty" data-key="empty">No catches yet.</p>`;
  // The best grade of the session wears a small trophy, whatever the sort.
  const best = list.reduce((b, x) => (!b || x.info.score > b.info.score ? x : b), null as Row | null);
  const rows = list.slice(0, 100).map(({ c, at, sold, info }) => {
    const kept = !sold && S.creatures.has(c.id);
    if (!kept) setSnapshot(`cap${c.id}`, c);
    const anchor = kept ? `data-open="${c.id}"` : `data-snap="cap${c.id}"`;
    return `<div class="cap${sold ? ' sold' : ''}" data-key="c${c.id}" ${anchor}><img src="${spriteUrl(myArt(c.species, c.rolls.aura), c.rolls.aura)}" alt="">
      <div class="cap-main"><div class="cap-top"><b>${esc(cname(c))}</b><span class="lv">Lv ${c.level}</span>${best && best.c.id === c.id && list.length > 1 ? `<span class="cap-best" aria-label="Best">${ICON.trophy}</span>` : ''}${sold ? `<span class="muted">released · +${full(sold)}</span>` : ''}<span class="spacer"></span><time>${new Date(at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time></div>${info.pills}</div></div>`;
  }).join('');
  return `${sorts}<div class="cap-list" data-key="list">${rows}</div>${list.length > 100 ? `<p class="cap-empty" data-key="more">Showing 100 of ${full(list.length)}.</p>` : ''}`;
}

const nameOfWild = new Map<number, { species: string; level: number }>();
const sealOfWild = new Map<number, string>();
/** Shrine XP gets a line when the hunt or the seating changes, then every 50 kills. */
let shrineSig = '', shrineKills = 0;

export function hudEvents(evs: GameEvent[]) {
  loadSession();
  const leadId = S.field?.lead?.id;
  for (const e of evs) {
    switch (e.k) {
      case 'hit':
        if (e.by === 'lead' && !e.splash) {
          const w = nameOfWild.get(e.uid) ?? S.field?.wilds.find((x) => x.uid === e.uid);
          fight = { mv: e.mv, dmg: e.dmg, crit: e.crit, mult: e.mult, wild: w ? SPECIES[w.species]?.name ?? 'the wild' : 'the wild', at: Date.now() };
        }
        break;
      case 'spawn':
        nameOfWild.set(e.wild.uid, { species: e.wild.species, level: e.wild.level });
        if (nameOfWild.size > 200) nameOfWild.delete(nameOfWild.keys().next().value!);
        if (e.wild.aura) { session.aurasSeen++; push(`An aura ${esc(SPECIES[e.wild.species].name)} appeared!`, 'gold'); }
        break;
      case 'down': {
        session.kills++; session.xp += e.xp; session.coins += e.coins; lastGain = e.xp;
        killSeq++;
        noteRecent({ xp: e.xp, coins: e.coins, kills: 1, lead: leadId, cxp: e.cxp ?? e.xp });
        const lead = leadId !== undefined ? S.creatures.get(leadId) : undefined;
        if (lead) {
          session.leadXp[lead.id] = (session.leadXp[lead.id] ?? 0) + (e.cxp ?? e.xp);
          session.leadFrom[lead.id] ??= lead.level;
        }
        const w = nameOfWild.get(e.uid) ?? S.field?.wilds.find((x) => x.uid === e.uid);
        const species = w?.species ?? '';
        const now = Date.now();
        let l = killLine && killLine.species === species && now - killLine.at < MERGE_MS && log.includes(killLine) ? killLine : null;
        if (l) { l.n++; touch(l); } else l = killLine = add({ kind: 'kill', cls: '', species });
        l.xp += e.xp; l.coins += e.coins; l.who = lead ? cname(lead) : undefined;
        for (const d of e.drops) { const n = ITEM[d.item]?.name ?? d.item; l.drops[n] = (l.drops[n] ?? 0) + d.qty; }
        if (e.relic) l.drops['a relic'] = (l.drops['a relic'] ?? 0) + 1;
        if (e.bench?.length) {
          for (const [id, xp] of e.bench) { session.benchXp += xp; if (!session.benchIds.includes(id)) session.benchIds.push(id); }
          const sig = `${S.trainer?.ground}|${e.bench.map(([id]) => id).join(',')}`;
          if (sig !== shrineSig || ++shrineKills >= 50) {
            shrineSig = sig; shrineKills = 0;
            const parts = e.bench.map(([id, xp]) => { const c = S.creatures.get(id); return c ? `${nameLink(c)} +${full(xp)}` : ''; }).filter(Boolean);
            if (parts.length) { add({ kind: 'text', cls: 'dim', text: `Shrine XP per kill: ${parts.join(' · ')}` }); }
          }
        }
        break;
      }
      case 'throw':
        session.seals[e.seal] = (session.seals[e.seal] ?? 0) + 1;
        noteRecent({ xp: 0, coins: -(ITEM[e.seal]?.shopPrice ?? 0), kills: 0 });
        sealOfWild.set(e.uid, e.seal);
        if (sealOfWild.size > 200) sealOfWild.delete(sealOfWild.keys().next().value!);
        throws.push(Date.now());
        if (throws.length > 400) throws.splice(0, 200);
        break;
      case 'potion': session.potions[e.item] = (session.potions[e.item] ?? 0) + 1; noteRecent({ xp: 0, coins: -(shopPriceAt(e.item, S.trainer?.level ?? 1) ?? 0), kills: 0 }); { const c = S.creatures.get(e.id); push(`${esc(ITEM[e.item]?.name ?? 'Potion')} used${c ? ` on ${nameLink(c)}` : ''}`, 'dim'); } break;
      case 'catch': {
        session.catches++;
        if (e.creature.rolls.aura) session.aurasCaught++;
        const s = summary(e.creature);
        const gr = gradeOf(s.rating);
        session.grades[gr] = (session.grades[gr] ?? 0) + 1;
        session.caught.push({ c: e.creature, at: Date.now(), ...(e.sold ? { sold: e.sold } : {}) });
        if (session.caught.length > CAUGHT_KEEP) session.caught.splice(0, session.caught.length - CAUGHT_KEEP);
        if (e.sold) { session.coins += e.sold; noteRecent({ xp: 0, coins: e.sold, kills: 0 }); push(`Released ${esc(cname(e.creature))} ${gradeBadge(s.rating, 'sm')} · +${full(e.sold)} coins`, 'dim'); break; }
        const first = (S.trainer?.dex[e.creature.species]?.caught ?? 0) <= 1;
        const team = S.trainer?.team.includes(e.creature.id);
        push(`Caught ${nameLink(e.creature)} Lv ${e.creature.level} ${miniPills(e.creature)} <span class="muted">${[team ? 'to team' : '', first ? 'new species' : ''].filter(Boolean).join(' · ')}</span>`, e.creature.rolls.aura || e.creature.rolls.bloodline >= 3 ? 'gold' : 'catch');
        break;
      }
      case 'miss': {
        const w = nameOfWild.get(e.uid);
        const seal = sealOfWild.get(e.uid) ?? S.trainer?.settings.seals[0]?.id ?? '';
        const now = Date.now();
        if (missLine && missLine.seal === seal && now - missLine.at < MERGE_MS && log.includes(missLine)) { missLine.n++; touch(missLine); }
        else missLine = add({ kind: 'miss', cls: 'dim', seal, species: w?.species });
        break;
      }
      case 'faint': { const c = S.creatures.get(e.id); push(`${c ? nameLink(c) : 'Your creature'} fainted`, 'bad'); break; }
      case 'swap': {
        // "Fenrisborn fainted · Muspel Brute takes over"
        const last = log[log.length - 1];
        const c = S.creatures.get(e.id);
        if (last?.cls === 'bad' && last.text?.endsWith(' fainted') && c) last.text += ` · ${nameLink(c)} takes over`;
        break;
      }
      case 'revive':
        fight = null;
        push(`Team knocked out · Revive used, back at ${Math.round(REVIVE_HP * 100)}% HP · ${e.left} left`, e.left ? 'dim' : 'bad');
        break;
      case 'restock':
        push(`Auto-restock · ${e.items.map((b) => `${num(b.qty)} ${esc(ITEM[b.id]?.name ?? b.id)}`).join(', ')} · −${full(e.coins)} coins`, 'dim');
        break;
      case 'rest':
        fight = null;
        if (e.xpLost !== undefined) { session.knockouts++; session.xpLost += e.xpLost; noteRecent({ xp: -e.xpLost, coins: 0, kills: 0 }); }
        push(`Team knocked out · ${e.xpLost ? `−${full(e.xpLost)} XP · ` : ''}back in ${Math.round(CONFIG.allFaintedRestMs / 1000)} s`, 'bad');
        break;
      case 'stopped':
        fight = null;
        session.knockouts++; session.xpLost += e.xpLost; noteRecent({ xp: -e.xpLost, coins: 0, kills: 0 });
        push(`Team knocked out · ${e.xpLost ? `−${full(e.xpLost)} XP · ` : ''}no potions or Revives · back to Kinhall`, 'bad');
        break;
      case 'retreat': {
        const to = GROUND[e.to];
        push(`${CONFIG.wipeRetreatCount} knockouts · moved to ${to ? `${esc(AREA[to.area].name)} (Lv ${to.level})` : 'an easier hunt'}`, 'warn');
        break;
      }
      case 'move': {
        const to = GROUND[e.to];
        push(`Auto-move → ${to ? `${esc(AREA[to.area].name)} (Lv ${to.level})` : 'a better hunt'} · best ${e.by === 'xp' ? 'XP' : 'coins'}`, 'warn');
        break;
      }
      case 'levelup':
        // A quiet line for the level itself; what it opens (regions, hunts, systems) gets its own line.
        push(`Trainer Lv ${full(e.level)}${e.coins ? ` · +${full(e.coins)} coins` : ''}`, 'dim');
        for (const u of opensBetween(e.level - 1, e.level)) push(`Unlocked: ${esc(u.name)} · ${esc(u.where)}`, 'gold');
        for (const a of areasBetween(e.level - 1, e.level)) push(`New hunts: ${esc(a.name)} (Lv ${full(a.level)})`, 'lvl');
        break;
      case 'hatch': {
        // A hatch is a log line with a Show in chat button; nothing pops up.
        const c = e.creature, s = summary(c), egg = EGG[e.egg];
        push(`Hatched ${nameLink(c)} ${gradeBadge(s.rating, 'sm')}${crest(c.rolls.bloodline)} · <button class="logbtn" data-act="showcase" data-id="${c.id}">Show in chat</button> <span class="muted">${esc(egg?.name ?? 'Coin egg')}${c.devGranted ? ', admin-flagged' : ''}</span>`, 'gold');
        break;
      }
      case 'clevel': { const c = S.creatures.get(e.id); push(`${c ? nameLink(c) : 'A creature'} reached Lv ${e.level}`, 'lvl'); break; }
      default: {
        // Events this panel doesn't know yet still get a readable line if they carry text.
        const any = e as { k: string; text?: string };
        if (any.text) push(esc(any.text));
      }
    }
  }
  saveSession();
  if (!document.hidden) renderLog();
}
document.addEventListener('visibilitychange', () => { if (!document.hidden) renderLog(); });

/** Ticks the session clock once a second. */
export function startHudClock() {
  setInterval(() => { if (!document.hidden && S.page === 'hunt') renderSession(); }, 1000);
}

